// THE SIDEBAR'S CONTENTS, and who each item is worth showing to.
//
// Split out of Layout so the rule can be tested on its own (navItems.test.js) rather than
// inferred by rendering a shell.
//
// ⚠ THIS IS COSMETIC. IT ENFORCES NOTHING, AND IT MUST NEVER BE THE THING THAT DOES.
// Every screen behind these links calls an API that is classified in the server's one
// mount table, and that table refuses on every request. Hiding a link spares somebody a
// page that would answer 403 to everything it asked; it is not what keeps them out. The
// order matters: the server's guard is the boundary, this list is a courtesy, and the day
// the two disagree the server wins and the user sees a refusal — which is the correct
// failure.
//
// ⚠ EVERY ITEM DECLARES ITS AUDIENCE, AND THE DEFAULT IS STAFF.
//
// It used to be the opposite — an item with no `roles` showed to anyone who could see the
// shell — and that was wrong in exactly the way a permissive default always is: a content
// writer logged in and got the entire menu, every link answering "אין לך הרשאה למסך הזה".
// The server held, so nothing leaked, but the app read as broken. `roles` is now required
// on every row and `navItemsFor` refuses a row without one, so a screen added later
// cannot inherit an audience by omission.
//
// Note this default is STAFF while the SERVER's default is partner-only. They differ
// deliberately: forgetting to classify an API mount must fail closed, whereas forgetting
// to classify a menu item should show it to the people who run the business and let the
// server sort out the rest. Neither default is a permission.
import he from '../i18n/he';
import { ROLE, ROLES, STAFF } from '../utils/roles';

// `alert` names which tally this item's dot reads — the server's own board names, plus
// 'money' for the invoice side. An item without one carries no dot: nothing on that
// screen can be late. The dot and the bell come from the SAME provider, so a dot beside
// לידים always has a matching row in the bell.
export const ITEMS = [
  // THE LANDING PAGE, and the only item with no dot despite gathering everything that
  // could carry one. Its dot would be the loudest of the three below it every time one of
  // them lit up, directly under a bell already saying the same thing — a signal that is
  // always on is one that stops being read. The page itself is where the detail is.
  { to: '/', label: he.myDay.nav, end: true, roles: STAFF },
  { to: '/board', label: he.nav.board, alert: 'customers', roles: STAFF },
  // Beside the board, because they are the same daily question asked twice: the board is
  // what needs doing today, the diary is where I need to BE today. No `alert` key, and
  // that follows the rule above rather than being an omission — a meeting cannot be late.
  { to: '/calendar', label: he.calendar.nav, roles: STAFF },
  // The pinboard, beside the day and the diary because it is the same daily question in
  // its third form: what needs doing, where I need to be, and what somebody left me. No
  // `alert` key, and that follows the rule rather than being an omission — a note has no
  // due date and cannot be late, which is exactly what separates it from a task.
  { to: '/notes', label: he.notes.nav, roles: STAFF },
  { to: '/clients', label: he.nav.clients, roles: STAFF },
  { to: '/leads', label: he.nav.leads, alert: 'leads', roles: STAFF },

  { to: '/retainers', label: he.nav.retainers, roles: STAFF },
  { to: '/jobs', label: he.nav.jobs, roles: STAFF },
  // Every task that holds a tracking list (078), beside the work it belongs to. No `alert`
  // key: a list has no date of its own and cannot be late — its task carries the due date.
  { to: '/lists', label: he.lists.nav, roles: STAFF },
  // The one money screen: what has been billed and is still owed, and what has not been
  // billed yet but should be. The path stays `/money` because that is what it is.
  { to: '/money', label: he.nav.money, alert: 'money', roles: STAFF },

  // ⚠ STAFF, INCLUDING THE SECRETARY. The gap between the two owners is not hidden from
  // her: she manages the financial data day to day and this is part of it. The line
  // between her and a partner is that she can never be ATTRIBUTED money — no share, never
  // an issuer — which the schema enforces, not this list.
  { to: '/settlements', label: he.nav.settlements, roles: STAFF },
  { to: '/dashboard', label: he.nav.dashboard, roles: STAFF },
  // EVERY expense the business has, office overhead and the costs on jobs alike — they
  // are one table (migration 026) and one question: what do we still owe. The mirror of
  // the money screen above, which answers who owes US.
  { to: '/expenses', label: he.nav.expenses, roles: STAFF },
  { to: '/documents', label: he.nav.documents, roles: STAFF },
  // Beside the office filing cabinet, because they are the same question split the way
  // the files themselves are: that one holds what belongs to no client, this one holds
  // every file that belongs to one, gathered from the four tables that own them.
  { to: '/client-documents', label: he.nav.clientDocuments, roles: STAFF },
  { to: '/settings', label: he.nav.settings, roles: STAFF },

  // Partner only — the screen behind it calls `/admin/users`, which is mounted
  // partner-only on the server. Hiding the link spares a secretary a page that would 403
  // on every request; the mount is what actually refuses her.
  { to: '/users', label: he.users.nav, roles: [ROLE.PARTNER] },
];

// The items one role should be shown. A row with no `roles`, or with a role that is not a
// role, is a mistake rather than a wildcard — it throws at import time instead of
// quietly showing a link to everybody.
export const navItemsFor = (role) => ITEMS.filter((item) => {
  if (!Array.isArray(item.roles) || item.roles.length === 0) {
    throw new Error(`nav item ${item.to} declares no roles`);
  }
  const unknown = item.roles.filter((r) => !ROLES.includes(r));
  if (unknown.length > 0) {
    throw new Error(`nav item ${item.to} names unknown role(s) ${unknown.join(', ')}`);
  }
  return item.roles.includes(role);
});
